import { rest } from "msw";

import {
  SignupVariables,
  StartCCPAVariables,
  UserProfileVariables,
} from "../../hooks";
import serverConfig from "../serverConfig";
import applications from "./applications";
import auth from "./auth";
import cards from "./cards";
import config from "./config";
import content from "./content";
import deactivation from "./deactivation";
import events from "./events";
import offers from "./offers";
import outcomeDiaries from "./outcomeDiaries";
import preferences from "./preferences";
import profile from "./profile";
import programs from "./programs";
import rewards from "./rewards";
import scripthero from "./scripthero";
import shared from "./shared";
import user from "./user";

export const handlers = [
  /*
  /** APPLICATIONS */
  rest.get(
    `${serverConfig.test.domain}/v7/applications`,
    (request, response, context) => {
      const { searchParams } = request.url;
      let platform = searchParams.get("platform");

      return response(
        context.status(200),
        context.json(applications.get(platform))
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/applications/:applicationId`,
    (request, response, context) => {
      const { applicationId } = request.params;

      return response(
        context.status(applicationId === "10000" ? 404 : 200),
        context.json(applications.getById(applicationId as string))
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/applications/:applicationId/connect`,
    (request, response, context) => {
      const { applicationId } = request.params;

      return response(
        context.status(200),
        context.json(applications.connect(applicationId as string))
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/applications/:applicationId/disconnect`,
    (request, response, context) => {
      const { applicationId } = request.params;

      return response(
        context.status(200),
        context.json(applications.disconnect(applicationId as string))
      );
    }
  ),

  /*
  /** AUTH */
  rest.post<{ user: SignupVariables }>(
    `${serverConfig.test.domain}/v7/auth`,
    (request, response, context) => {
      if (request.body?.user?.email === "conflict@evidation.com") {
        return response(context.status(409), context.json(auth.signupError()));
      }
      return response(context.status(200), context.json(auth.signup()));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/auth/login`,
    (_, response, context) => {
      return response(context.status(200), context.json(auth.login()));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/auth/logout`,
    (_, response, context) => {
      return response(context.status(200));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/auth/change_password`,
    (_request, response, context) => {
      return response(
        context.status(200),
        context.json(auth.getUpdatePassword())
      );
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/auth/reset_password`,
    (_request, response, context) => {
      return response(
        context.status(200),
        context.json(auth.getResetPassword())
      );
    }
  ),

  /*
  /** CONFIG */
  rest.get(`${serverConfig.test.domain}/v7/config`, (_, response, context) => {
    return response(context.status(200), context.json(config.get()));
  }),

  /*
  /** CONTENT */
  rest.get(
    `${serverConfig.test.domain}/v7/content/:contentConnectionId/:contentIdentifier`,
    (request, response, context) => {
      const { contentIdentifier } = request.params;

      try {
        const json = content.getByIdentifier(contentIdentifier as string);
        return response(context.status(200), context.json(json));
      } catch (error) {
        console.error(error);
        return response(context.status(500));
      }
    }
  ),

  /*
  /** DEACTIVATION */
  rest.post(
    `${serverConfig.test.domain}/v7/deactivation`,
    (_, response, context) => {
      return response(context.status(200), context.json(deactivation.post()));
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/deactivation/reasons`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(deactivation.getReasons())
      );
    }
  ),

  /*
  /** EVENTS */
  rest.get(
    `${serverConfig.test.domain}/v7/events/download`,
    (_, response, context) => {
      return response(context.status(200), context.json(events.export()));
    }
  ),

  rest.get(`${serverConfig.test.domain}/v7/events`, (_, response, context) => {
    return response(context.status(200), context.json(events.get()));
  }),

  rest.get(
    `${serverConfig.test.domain}/v7/events/aggregate`,
    (_, response, context) => {
      return response(context.status(200), context.json(events.aggregate()));
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/events/points_history`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(events.pointsHistory())
      );
    }
  ),

  /*
  /** OFFERS */
  rest.get(
    `${serverConfig.test.domain}/v7/offers/:offerIdentifier`,
    (request, response, context) => {
      const { offerIdentifier } = request.params;

      return response(
        context.status(200),
        context.json(offers.getOne(offerIdentifier as string))
      );
    }
  ),

  rest.get(`${serverConfig.test.domain}/v7/offers`, (_, response, context) => {
    return response(context.status(200), context.json(offers.get()));
  }),

  rest.post(
    `${serverConfig.test.domain}/v7/offers/invite`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(offers.getOne("program-enrollment"))
      );
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/offers/:offerIdentifier/activity_feed_click`,
    (_, response, context) => {
      return response(
        context.status(201),
        context.json(shared.getEmptyResponse())
      );
    }
  ),

  /*
  /** CARDS */
  rest.get(`${serverConfig.test.domain}/v7/cards`, (_, response, context) => {
    return response(context.status(200), context.json(cards.get()));
  }),

  rest.post(
    `${serverConfig.test.domain}/v7/offers/:offerIdentifier/card_click`,
    (_, response, context) => {
      return response(context.status(200), context.json(cards.click()));
    }
  ),

  /*
  /** PREFERENCES */
  rest.get(
    `${serverConfig.test.domain}/v7/preferences`,
    (_, response, context) => {
      return response(context.status(200), context.json(preferences.get()));
    }
  ),

  rest.put(
    `${serverConfig.test.domain}/v7/preferences`,
    (request, response, context) => {
      return response(
        context.status(200),
        context.json(preferences.update(request.body))
      );
    }
  ),

  rest.patch(
    `${serverConfig.test.domain}/v7/programs/:programId/preferences`,
    (request, response, context) => {
      return response(
        context.status(200),
        context.json(preferences.updateProgram(request.body))
      );
    }
  ),

  /*
  /** PROGRAMS */
  rest.get(
    `${serverConfig.test.domain}/v7/programs`,
    (_, response, context) => {
      return response(context.status(200), context.json(programs.get()));
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/programs/:programIdentifier`,
    (request, response, context) => {
      const { programIdentifier } = request.params;

      const { searchParams } = request.url;
      const offerIdentifier = searchParams.get("offer_identifier");

      return response(
        context.status(200),
        context.json(
          programs.getOne(
            programIdentifier as string,
            offerIdentifier as string
          )
        )
      );
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/programs/:programIdentifier/reports/send`,
    (_, response, context) => {
      return response(context.status(201));
    }
  ),

  rest.put(
    `${serverConfig.test.domain}/v7/programs/:programIdentifier/unenroll`,
    (_, response, context) => {
      return response(context.status(201));
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/outcome_diaries/:diaryIdentifier`,
    (request, response, context) => {
      const { diaryIdentifier } = request.params;
      return response(
        context.status(200),
        context.json(outcomeDiaries.getOutcomeDiary(diaryIdentifier as string))
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/outcome_diaries/outcomes/:outcomeID/insights`,
    (request, response, context) => {
      const { outcomeID } = request.params;
      return response(
        context.status(200),
        context.json(outcomeDiaries.getOutcomeDiaryInsight(outcomeID as string))
      );
    }
  ),

  rest.put(
    `${serverConfig.test.domain}/v7/outcome_diaries/outcomes/:outcomeID`,
    (request, response, context) => {
      const { outcomeID } = request.params;
      return response(
        context.status(200),
        context.json(
          outcomeDiaries.getPatientReportedOutcome(outcomeID as string)
        )
      );
    }
  ),

  /*
  /** REWARDS */
  rest.get(`${serverConfig.test.domain}/v7/rewards`, (_, response, context) => {
    return response(context.status(200), context.json(rewards.get()));
  }),

  rest.post(
    `${serverConfig.test.domain}/v7/rewards`,
    (request, response, context) => {
      // @ts-ignore
      const { _error } = request.body?.data;

      if (_error && typeof _error === "string") {
        return response(
          context.status(400),
          context.json(rewards.getError(_error))
        );
      }

      return response(context.json(200), context.json(profile.get()));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/rewards/1/resend`,
    (_, response, context) => {
      return response(context.status(200), context.json(rewards.resend()));
    }
  ),

  /*
  /** SCRIPTHERO */
  rest.get(
    `${serverConfig.test.domain}/v7/scripthero/discounts`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(scripthero.getDiscounts())
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/scripthero/pharmacies`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(scripthero.getPharmacies())
      );
    }
  ),

  /*
  /** USER */
  rest.get(`${serverConfig.test.domain}/v7/profile`, (_, response, context) => {
    return response(context.status(200), context.json(profile.get()));
  }),

  rest.put<{ user: UserProfileVariables }>(
    `${serverConfig.test.domain}/v7/profile`,
    (request, response, context) => {
      return response(
        context.status(200),
        context.json(
          request.body?.user?.email
            ? profile.updateEmail()
            : profile.update(request.body)
        )
      );
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/user/devices`,
    (request, response, context) => {
      return response(
        context.status(200),
        context.json(
          //@ts-ignore JSON:API serialized body typing
          Array.isArray(request.body.data)
            ? user.getUserDevices()
            : user.getUserDevice()
        )
      );
    }
  ),

  rest.post<StartCCPAVariables>(
    `${serverConfig.test.domain}/v7/user/start_ccpa_process`,
    (request, response, context) => {
      if (request.body.function === "delete") {
        return response(
          context.status(200),
          context.json(user.getStartCCPADelete())
        );
      }
      return response(context.status(200));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/user/request_otp`,
    (_request, response, context) => {
      return response(context.status(200), context.json({ data: null }));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/user/verify_otp`,
    (_request, response, context) => {
      return response(
        context.status(200),
        context.json(user.getVerifiedProfile())
      );
    }
  ),

  rest.get(
    `${serverConfig.test.domain}/v7/user/points`,
    (_, response, context) => {
      return response(context.status(200), context.json(user.getUserPoints()));
    }
  ),

  rest.post(
    `${serverConfig.test.domain}/v7/user/resend_confirmation`,
    (_, response, context) => {
      return response(
        context.status(200),
        context.json(shared.getMetaMessageResponse())
      );
    }
  ),
];
